import { ReactElement } from 'react';
import { Alert, Box, Button, Stack, openModal } from '@ff-it/ui';
import { header } from 'styles/style.css';
import type { CorrectableInvoice, CorrectorData } from './types';
import { InvoiceWidget } from 'modules/invoicing/common/InvoiceWidget';
import { BlockWidget, BoundState } from 'components';
import { EnterpriseLink } from 'components/EnterpriseLink';
import { Icon } from 'components/Icon';
import { ProductTitle } from 'modules/supplier/products';
import { TitleContainer } from 'components/TitleContainer';
import { fmtMonth } from 'utilities';
import { ConfirmDialog } from 'components/Actions/createConfirmDialog';

type ContainerProps = {
  data: CorrectorData;
  handleCorrection: (invoice: CorrectableInvoice) => void;
};

export function Container({ data: { linkable, entities, invoices }, handleCorrection }: ContainerProps): ReactElement {
  const linkableMap = linkable.reduce((acc: Record<string, number>, v, idx) => {
    acc[v.id] = idx;
    return acc;
  }, {});

  if (invoices.length === 0) {
    return <Alert variant="info">There is no linked income with overspent rows</Alert>;
  }

  return (
    <Box data-testid="correct-overspent-container">
      <Alert variant="info">Align linked income of overspent rows by reducing amount.</Alert>
      {invoices.map((invoice) => {
        return (
          <Box key={invoice.id} mb="4" border="default">
            <Stack direction="horizontal" className={header.default} p="2">
              <InvoiceWidget invoice={invoice} link />
              <Box marginLeft="auto">
                <Button
                  testId={`correct-invoice-${invoice.id}`}
                  size="sm"
                  onClick={async () => {
                    const confirm = await openModal(
                      (render) => (
                        <ConfirmDialog {...render} title="Are you sure?">
                          This is going to modify invoice links <br /> and mark invoice as <em>not linked</em>.
                        </ConfirmDialog>
                      ),
                      { testId: 'confirm-reduce', canDismiss: false },
                    );
                    if (confirm) {
                      handleCorrection(invoice);
                    }
                  }}
                >
                  Reduce
                </Button>
              </Box>
            </Stack>
            <Box>
              {invoice.rows.map(({ id, month, amount: _, overspent }) => {
                const { campaign_id, block_id, product_id, provider_id, activity_id } = linkable[linkableMap[id]];
                return (
                  <Stack direction="horizontal" key={id} py="2" px="4">
                    <Box flexShrink={0} pr="3" display="flex" alignItems="center">
                      <BoundState variant="income" state="FULL" is_overspent row={id} />
                    </Box>
                    <Box flexShrink={0} pr="3" display="flex" alignItems="center">
                      <EnterpriseLink to={`/planning/campaigns/${campaign_id}/planning/${block_id}?rowId=${id}`}>
                        <Icon size="sm" icon="link" />
                      </EnterpriseLink>
                    </Box>
                    <Box width="full">
                      <BlockWidget {...entities.block[block_id]} link />
                    </Box>
                    <Box width="full">
                      <ProductTitle product={entities.product[product_id]} />
                    </Box>
                    <Box width="full">
                      <TitleContainer
                        title={entities.company[provider_id].title}
                        subtitle={entities.activity[activity_id].name}
                      />
                    </Box>
                    <Box width="full">
                      <Stack direction="horizontal">
                        <Box width="full" textAlign="right">
                          {fmtMonth(month)}
                        </Box>
                        <Box width="full" textAlign="right" fontWeight="bold">
                          {overspent}
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
